// Consent, as React state that is also on disk.
//
// **One owner.** Consent is read by the thing that submits, written by the
// dialog and by the settings panel, and asked about by whatever decides to put
// the question — three readers and two writers, which is exactly the shape
// that ends up with two disagreeing copies. So `localStorage` is written
// through here and nowhere else.
//
// The `ref` is not a duplicate of the state, it is the *current* value for
// callbacks that outlive the render they were made in — a game ending is
// resolved in an effect or a socket handler, and a stale closure there would
// submit under an answer the player has since withdrawn. It is read at the
// moment of sending, never captured.

import { useCallback, useRef, useState } from "react";

import {
  answerConsent,
  type Consent,
  readConsent,
  shouldAsk,
  writeConsent
} from "src/engine/telemetry/consent";

export interface ConsentControl {
  consent: Consent;
  /** The live value, for callbacks that may be older than the last render. */
  current(): Consent;
  /** True when this browser has never been asked under the current disclosure. */
  unanswered: boolean;
  answer(agreed: boolean): void;
}

export function useConsent(): ConsentControl {
  const [consent, setConsent] = useState<Consent>(() => readConsent());
  const live = useRef(consent);
  live.current = consent;

  const answer = useCallback((agreed: boolean) => {
    setConsent((was) => {
      const next = answerConsent(was, agreed);
      writeConsent(next);
      live.current = next;
      return next;
    });
  }, []);

  return {
    consent,
    current: useCallback(() => live.current, []),
    unanswered: shouldAsk(consent),
    answer
  };
}
